import React, { useState, useRef, useEffect } from 'react';
import { Eye, ChevronDown } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import { fetchChatLogs, fetchChatLogById, ChatLogListItem, ChatLogMessageItem, exportChatLogsCsv } from '../../services/api';
import { useTimezoneFormatting } from '../../hooks/useTimezoneFormatting';
import Preloader from '../../components/Preloder';
import { logger } from '../../utils/logger';
import { formatAIText } from 'src/utils/formatAIText';
import { formatSopReferences, resolveAssistantSourceLine } from '../../utils/chatLogSource';

type Role = 'manager' | 'worker' | 'admin';

interface ChatLog {
    id: string;
    title: string;
    user: string;
    role: Role;
    date: string; // ISO date string
    sopReferenced: string;
    documentNames?: string;
    warnings: number;
}

// Backend-driven items
type ListResponse = { items: ChatLogListItem[]; total: number };

function escapeHtmlForPdf(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export const AdminReports: React.FC = () => {
    const { formatToLocal } = useTimezoneFormatting();
    const [search, setSearch] = useState('');
    const [role, setRole] = useState<'all' | Role>('all');
    const [page, setPage] = useState(1);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [viewChatModalOpen, setViewChatModalOpen] = useState(false);
    const [selectedChat, setSelectedChat] = useState<ChatLog | null>(null);
    const [items, setItems] = useState<ChatLogListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pageSize, setPageSize] = useState(9);
    const roleDropdownRef = useRef<HTMLDivElement>(null);
    const [selectedMessages, setSelectedMessages] = useState<ChatLogMessageItem[]>([]);
    const [isExporting, setIsExporting] = useState(false);

    // Fetch logs from backend whenever filters/page change
    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true); setError(null);
            try {
                // Use fetchChatLogs to get all chat logs (including manager chats)
                const res: ListResponse = await fetchChatLogs({ page, pageSize, search, role: role === 'all' ? undefined : role });
                if (!cancelled) { setItems(res.items || []); setTotal(res.total || 0); }
            } catch (e: any) {
                if (!cancelled) setError(e?.message || 'Failed to load chat logs');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setInitialLoading(false); // Set initial loading to false after first load
                }
            }
        }
        load();
        return () => { cancelled = true; };
    }, [page, pageSize, search, role]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const pageItems = items; // already server-paginated

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
                setIsRoleDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    async function exportCsv() {
        setIsExporting(true);
        try {
            const blob = await exportChatLogsCsv({ search, role: role === 'all' ? undefined : role });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `chat-logs-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            logger.error('Export failed', e);
            alert('Export failed. Please check the console for details.');
        } finally {
            setIsExporting(false);
        }
    }

    // Show preloader while data is loading
    if (initialLoading) {
        return <Preloader />;
    }

    return (
        <div className="space-y-6 min-h-0">
            {/* Controls */}
            <div className="px-0">
                <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-center md:justify-between pt-1 md:pt-2">
                    <div className="flex items-center gap-3">
                        <div className="w-full md:w-80 shrink-0 pl-2 md:pl-3">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                                    <img src="/icons/searchIcon.png" alt="Search" className="w-4 h-4" />
                                </span>
                                <input
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                    placeholder="Search"
                                    className="w-full h-10 pl-9 pr-4 py-1 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                                />
                            </div>
                        </div>
                        <div className="relative" ref={roleDropdownRef}>
                            <button
                                onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                                className="w-48 h-10 px-3 py-2 flex items-center justify-between bg-white border border-gray-300 rounded-xl text-sm text-gray-700 transition-colors shadow-sm hover:bg-gray-50"
                            >
                                <span className={`${role === 'all' ? 'text-gray-500' : 'text-gray-700'} font-normal`}>{role === 'all' ? 'Select User Type' : role === 'manager' ? 'Manager' : 'Worker'}</span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isRoleDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-lg z-10">
                                    <div className="py-1">
                                        <button
                                            onClick={() => { setRole('all'); setIsRoleDropdownOpen(false); setPage(1); }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Select User Type
                                        </button>
                                        <button
                                            onClick={() => { setRole('manager'); setIsRoleDropdownOpen(false); setPage(1); }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Manager
                                        </button>
                                        <button
                                            onClick={() => { setRole('worker'); setIsRoleDropdownOpen(false); setPage(1); }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Worker
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex-none pr-2 md:pr-3">
                        <button
                            onClick={exportCsv}
                            disabled={isExporting}
                            className="renegade-btn-primary mini-btn"
                        >
                            {isExporting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21.7812 12.1249V23.3749C21.7812 23.9965 21.5343 24.5926 21.0948 25.0322C20.6552 25.4717 20.0591 25.7186 19.4375 25.7186H2.5625C1.9409 25.7186 1.34476 25.4717 0.905218 25.0322C0.46568 24.5926 0.21875 23.9965 0.21875 23.3749V12.1249C0.21875 11.5033 0.46568 10.9071 0.905218 10.4676C1.34476 10.0281 1.9409 9.78114 2.5625 9.78114H4.90625C5.27921 9.78114 5.6369 9.9293 5.90062 10.193C6.16434 10.4567 6.3125 10.8144 6.3125 11.1874C6.3125 11.5603 6.16434 11.918 5.90062 12.1818C5.6369 12.4455 5.27921 12.5936 4.90625 12.5936H3.03125V22.9061H18.9688V12.5936H17.0938C16.7208 12.5936 16.3631 12.4455 16.0994 12.1818C15.8357 11.918 15.6875 11.5603 15.6875 11.1874C15.6875 10.8144 15.8357 10.4567 16.0994 10.193C16.3631 9.9293 16.7208 9.78114 17.0938 9.78114H19.4375C20.0591 9.78114 20.6552 10.0281 21.0948 10.4676C21.5343 10.9071 21.7812 11.5033 21.7812 12.1249ZM7.30742 7.49481L9.59375 5.21083V14.9374C9.59375 15.3103 9.74191 15.668 10.0056 15.9318C10.2694 16.1955 10.627 16.3436 11 16.3436C11.373 16.3436 11.7306 16.1955 11.9944 15.9318C12.2581 15.668 12.4062 15.3103 12.4062 14.9374V5.21083L14.6926 7.49832C14.8234 7.62913 14.9787 7.7329 15.1496 7.80369C15.3205 7.87448 15.5037 7.91092 15.6887 7.91092C15.8737 7.91092 16.0568 7.87448 16.2278 7.80369C16.3987 7.7329 16.554 7.62913 16.6848 7.49832C16.8156 7.36752 16.9193 7.21222 16.9901 7.04131C17.0609 6.8704 17.0974 6.68722 17.0974 6.50223C17.0974 6.31724 17.0609 6.13406 16.9901 5.96315C16.9193 5.79224 16.8156 5.63695 16.6848 5.50614L11.9973 0.818638C11.8666 0.687538 11.7114 0.583518 11.5404 0.512541C11.3695 0.441565 11.1863 0.405029 11.0012 0.405029C10.8161 0.405029 10.6328 0.441565 10.4619 0.512541C10.291 0.583518 10.1357 0.687538 10.0051 0.818638L5.31758 5.50614C5.18677 5.63695 5.08301 5.79224 5.01221 5.96315C4.94142 6.13406 4.90498 6.31724 4.90498 6.50223C4.90498 6.87584 5.0534 7.23414 5.31758 7.49832C5.58176 7.76251 5.94006 7.91092 6.31367 7.91092C6.68728 7.91092 7.04559 7.76251 7.30977 7.49832L7.30742 7.49481Z" fill="white" />
                                    </svg>
                                    Export Logs
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Chat Title</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">User</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Role</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Date</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">SOPs Referenced</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Warnings</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-4 text-14-bold grey-800">Loading...</td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-6 text-center text-red-600">{error}</td>
                                </tr>
                            ) : pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center !text-gray-500">No chats found</td>
                                </tr>
                            ) : (
                                pageItems.map((log) => (
                                    <tr key={log.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-14-bold grey-800 truncate max-w-[260px]" title={log.title}>{log.title}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{log.user}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{toTitle(log.role)}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800 text-nowrap">{formatDate(log.date)}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800 truncate max-w-[300px]" title={formatSopReferences(log.sopReferenced)}>{formatSopReferences(log.sopReferenced)}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{log.warnings}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <button
                                                className="p-2 rounded-lg hover:bg-gray-100"
                                                onClick={async () => {
                                                    try {
                                                        // Fetch from chat logs system (where manager chats are stored)
                                                        const response = await fetchChatLogById(log.id);
                                                        const full = response;
                                                        setSelectedChat({
                                                            id: full.id,
                                                            title: full.title,
                                                            user: full.user,
                                                            role: full.role as Role,
                                                            date: full.date,
                                                            sopReferenced: full.sopReferenced,
                                                            documentNames: (full as any).documentNames || '',
                                                            warnings: full.warnings,
                                                        });
                                                        setSelectedMessages(full.messages || []);
                                                        setViewChatModalOpen(true);
                                                    } catch (e) {
                                                        logger.error('Failed to load chat log', e);
                                                    }
                                                }}
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="global-pagination px-4 md:px-6 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            className="btn-next-prev"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <i className="iconoir-nav-arrow-left"></i>
                        </button>
                        <div className="flex items-center gap-1">
                            {getPageNumbers(page, totalPages).map((p: PageButton) => (
                                <button
                                    key={p.key}
                                    disabled={p.disabled}
                                    onClick={() => typeof p.page === 'number' && setPage(p.page)}
                                    className={`btn-page-number ${p.active ? 'color-renegade' : ''}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            className="btn-next-prev"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            <i className="iconoir-nav-arrow-right"></i>
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value) || 10;
                                if (newPageSize >= 1 && newPageSize <= 100) {
                                    setPageSize(newPageSize);
                                    setPage(1); // Reset to first page when page size changes
                                }
                            }}
                            className="page-size-input"
                        />
                        <span className="text-14-bold grey-400">Rows per page</span>
                    </div>
                </div>
            </div>

            {/* View Chat Modal */}
            {viewChatModalOpen && selectedChat && (
                <ViewChatModal
                    chat={selectedChat}
                    messages={selectedMessages}
                    onClose={() => {
                        setViewChatModalOpen(false);
                        setSelectedChat(null);
                        setSelectedMessages([]);
                    }}
                />
            )}
        </div>
    );
};

// View Chat Modal Component
function ViewChatModal({ chat, messages, onClose }: { chat: ChatLog; messages: ChatLogMessageItem[]; onClose: () => void }) {
    const [exportFormat, setExportFormat] = useState<'csv' | 'pdf' | 'json'>('csv');
    const [isExporting, setIsExporting] = useState(false);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
    const exportDropdownRef = useRef<HTMLDivElement>(null);
    const exportButtonRef = useRef<HTMLButtonElement>(null);

    // Check available space and position dropdown accordingly
    const checkDropdownPosition = () => {
        if (exportButtonRef.current) {
            const buttonRect = exportButtonRef.current.getBoundingClientRect();
            const modalBody = exportButtonRef.current.closest('.modal-body');


            if (modalBody) {
                const modalRect = modalBody.getBoundingClientRect();
                const spaceBelow = modalRect.bottom - buttonRect.bottom;
                const spaceAbove = buttonRect.top - modalRect.top;


                // Force to show on top for now to test
                setDropdownPosition('top');
            } else {
                // Fallback to top if modal not found
                setDropdownPosition('top');
            }
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
                setShowExportDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleExport = async (format: 'csv' | 'pdf' | 'json') => {
        setIsExporting(true);
        setShowExportDropdown(false);
        try {
            switch (format) {
                case 'csv':
                    exportChatAsCSV(chat);
                    break;
                case 'json':
                    exportChatAsJSON(chat);
                    break;
                case 'pdf':
                    await exportChatAsPDF(chat);
                    break;
            }
        } catch (error) {
            logger.error('Export failed:', error);
        } finally {
            setIsExporting(false);
            onClose();
        }
    };

    const exportChatAsCSV = (chat: ChatLog) => {
        const headers = ['Chat Title', 'User', 'Role', 'Date', 'SOP Referenced', 'Warnings', 'Conversation'];
        const conversation = messages.map(m => `${m.role === 'user' ? chat.user : 'AI'}: ${String(m.text).replace(/\n/g, ' ')}`).join(' | ');
        const row = [chat.title, chat.user, toTitle(chat.role), chat.date, chat.sopReferenced || '-', chat.warnings, conversation];
        const csv = [headers, row].map(r => r.map((value: string | number) => escapeCsv(String(value))).join(',')).join('\n');
        downloadFile(csv, `chat-${chat.id}.csv`, 'text/csv');
    };

    const exportChatAsJSON = (chat: ChatLog) => {
        const chatData = {
            id: chat.id,
            title: chat.title,
            user: chat.user,
            role: chat.role,
            date: chat.date,
            sopReferenced: chat.sopReferenced,
            warnings: chat.warnings,
            messages,
        };
        const json = JSON.stringify(chatData, null, 2);
        downloadFile(json, `chat-${chat.id}.json`, 'application/json');
    };

    const exportChatAsPDF = async (chat: ChatLog) => {
        // Generate HTML content from actual messages
        const messagesHtml = messages.map(m => {
            const isUser = m.role === 'user';
            const bubbleClass = isUser ? 'user-bubble' : 'ai-bubble';
            const senderName = isUser ? chat.user : 'AI Assistant';

            let messageContent = `<div class="chat-bubble ${bubbleClass}">
                <strong>${senderName}:</strong> ${String(m.text).replace(/\n/g, '<br>')}`;

            if (!isUser) {
                const srcLine = resolveAssistantSourceLine(m);
                if (srcLine) {
                    messageContent += `
                    <div class="sop-reference">
                        ${escapeHtmlForPdf(srcLine)}
                    </div>`;
                }
            }

            messageContent += '</div>';
            return messageContent;
        }).join('');

        // For PDF export, we'll create a simple HTML structure and use browser print
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Chat Export - ${chat.title}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        .header { border-bottom: 2px solid #dc2626; padding-bottom: 10px; margin-bottom: 20px; }
                        .chat-bubble { margin: 10px 0; padding: 10px; border-radius: 8px; }
                        .user-bubble { background: #dbeafe; text-align: right; }
                        .ai-bubble { background: #f3f4f6; }
                        .sop-reference { background: #eff6ff; padding: 10px; border: 1px solid #bfdbfe; border-radius: 8px; margin: 10px 0; }
                        @media print { body { margin: 0; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>${chat.title}</h1>
                        <p><strong>${chat.user}</strong> (${toTitle(chat.role)}) – ${formatDate(chat.date)}</p>
                    </div>
                    
                    ${messagesHtml}
                </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
        }
    };

    const downloadFile = (content: string, filename: string, mimeType: string) => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Modal show={true} onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">
                    {chat.title}... {chat.user} ({toTitle(chat.role)}) – {formatDate(chat.date)}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                {/* Chat Content */}
                <div className="overflow-y-auto pr-2" style={{ maxHeight: '70vh' }}>
                    <div className="space-y-4">
                        {messages.map((m, idx) => {
                            const assistantSourceLine =
                                m.role === 'assistant' ? resolveAssistantSourceLine(m) : null;
                            return (
                                <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div >
                                        {m.role === 'user' ? (
                                            <div className="text-16-bold grey-800 bg-grey-50 rounded-lg px-4 py-3">
                                                {m.text}
                                            </div>
                                        ) : (
                                            <div className="grey-700 admin-ai-chat-log">
                                                {formatAIText(m.text)}
                                            </div>
                                        )}
                                        {assistantSourceLine && (
                                            <div className="text-14-bold grey-800 max-w-[100%] mt-2">
                                                <span>{assistantSourceLine}</span>
                                            </div>
                                        )}
                                        <div className={`text-12-reg grey-700 ${m.role === 'user' ? 'text-right mt-2' : 'text-left mt-3'}`}>
                                            {m.role === 'user' ? `${chat.user}` : 'AI Assistant'}  [{formatTimestamp(m.timestamp)}]
                                        </div>



                                        {m.role === 'assistant' && m.text.includes('⚠️') && (
                                            <div className="mt-2 text-xs text-red-600">
                                                ⚠️ Warning detected
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Warnings/Flagged Section */}
                        {chat.warnings > 0 && (
                            <div className="mt-4 p-3 bg-red-50 rounded border border-red-200">
                                <div className="text-sm font-semibold text-red-800 mb-2">Warnings/Flagged</div>
                                <div className="space-y-1">
                                    {messages
                                        .filter(m => m.role === 'assistant' && m.text.includes('⚠️'))
                                        .map((m, idx) => (
                                            <div key={idx} className="text-sm text-red-700">
                                                {idx + 1}. {m.text.split('⚠️')[1]?.trim() || 'Warning detected'}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                {/* Footer with Action Buttons */}
                <div className="d-flex justify-content-end gap-3">
                    <button
                        onClick={onClose}
                        className="renegade-btn-secondary mini-btn"
                    >
                        Cancel
                    </button>
                    <div className="d-flex align-items-center gap-3">
                        <div className="position-relative" ref={exportDropdownRef}>
                            <button
                                ref={exportButtonRef}
                                onClick={() => setShowExportDropdown(!showExportDropdown)}
                                disabled={isExporting}
                                className="renegade-btn-primary mini-btn"
                            >
                                {isExporting ? 'Exporting...' : 'Export'}
                                <ChevronDown className={`w-6 h-6 text-light transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showExportDropdown && (
                                <div
                                    className="position-absolute start-0 bg-white border border-gray-300 rounded-lg shadow-lg z-10"
                                    style={{
                                        minWidth: '140px',
                                        bottom: '100%',   // ensures dropdown opens above
                                        marginBottom: '8px',
                                    }}
                                >
                                    <div className="py-1">
                                        <button
                                            onClick={() => handleExport('csv')}
                                            className="w-100 px-3 py-2 text-start text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Export as CSV
                                        </button>
                                        <button
                                            onClick={() => handleExport('json')}
                                            className="w-100 px-3 py-2 text-start text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Export as JSON
                                        </button>
                                        <button
                                            onClick={() => handleExport('pdf')}
                                            className="w-100 px-3 py-2 text-start text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-normal"
                                        >
                                            Export as PDF
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </Modal.Footer>
        </Modal>
    );
}

function toTitle(v: string) { return (v || '').replace(/_/g, ' ').replace(/^\w|\s\w/g, (m) => m.toUpperCase()); }
function formatDate(value?: string) {
    if (!value) return '-';
    // Ensure timestamp is treated as UTC
    const normalizedTimestamp = value.endsWith('Z') ? value : value + 'Z';
    const date = new Date(normalizedTimestamp);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}
function formatTimestamp(timestamp: string) {
    if (!timestamp) return '';
    // Ensure timestamp is treated as UTC
    const normalizedTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(normalizedTimestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
}

type PageButton = { key: string; page?: number; label: string | number; active?: boolean; disabled?: boolean };
function getPageNumbers(current: number, total: number): PageButton[] { const buttons: PageButton[] = []; const pushPage = (p: number, active = false) => buttons.push({ key: `p-${p}`, page: p, label: p, active }); const pushEllipsis = (id: string) => buttons.push({ key: `e-${id}`, label: '…', disabled: true }); if (total <= 5) { for (let i = 1; i <= total; i++) pushPage(i, i === current); return buttons; } pushPage(1, current === 1); if (current > 3) pushEllipsis('l'); const start = Math.max(2, current - 1); const end = Math.min(total - 1, current + 1); for (let i = start; i <= end; i++) pushPage(i, i === current); if (current < total - 2) pushEllipsis('r'); pushPage(total, current === total); return buttons; }
function escapeCsv(value: string) { if (/[",\n]/.test(value)) return '"' + value.replace(/"/g, '""') + '"'; return value; }
